/* eslint-disable import/no-cycle */
import React, {useState} from 'react';
import Highcharts from 'highcharts';
import {observer} from 'mobx-react-lite';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {useStore} from '../../../../hooks/useStore';
import {useMount} from '../../../../hooks/useMount';
import Chart from '../../../shared/charts/Chart';
import {dateFormat} from '../../../../config/moment';

const KpiChart = ({workplaceId, kpiId, daysBack, yAxisMax, yAxisTitle, showLegend, fromDate, toDate}) => {
  const store = useStore();
  const {t} = useTranslation();
  const [actualData, setActualData] = useState();
  const [targetData, setTargetData] = useState();
  const [unitOfMeasurement, setUnitOfMeasurement] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isTargetLineDisabled, setIsTargetLineDisabled] = useState(true);

  useMount(() => {
    (async () => {
      setIsLoading(true);

      const kpi = await store.keyPerformanceIndicatorStore.load(kpiId);
      let isPercent = false;

      if (kpi.unitOfMeasureId) {
        const uom = await store.unitOfMeasurementStore.load(kpi.unitOfMeasureId);
        setUnitOfMeasurement(uom);

        isPercent = !!uom.isPercent;
      } else {
        setUnitOfMeasurement(undefined);
      }

      if (workplaceId && fromDate && toDate && daysBack) {
        const actualResponse = await store.keyPerformanceIndicatorStore.loadKpiByTime({
          workplaceId,
          fromDate,
          toDate,
          resolution: daysBack,
          kpiId: [kpiId],
        });

        const targetResponse = await store.keyPerformanceIndicatorTargetStore.calculate({
          params: {
            workplaceId,
            fromDate,
            toDate,
            resolution: daysBack,
            kpiId: [kpiId],
          },
        });

        const actualResponseData = actualResponse && actualResponse[0]?.data;
        if (actualResponseData) {
          setActualData(
            actualResponseData.map((entry) => {
              const data = {
                x: moment(entry.x),
                y: entry.y ? entry.y : 0,
              };
              if (isPercent) {
                data.y *= 100;
              }
              return data;
            }).slice(0, actualResponseData.length - 1)
          );
        }

        const targetResponseData = targetResponse && targetResponse[0]?.series;
        if (targetResponseData) {
          const targetSum = targetResponseData.reduce((acc, curr) => acc + curr.y, 0);
          if (!targetSum) {
            setIsTargetLineDisabled(true);
          } else {
            setIsTargetLineDisabled(false);
          }

          setTargetData(
            targetResponseData.map((entry) => ({
              x: moment(entry.x),
              y: entry.y ? entry.y : 0,
            }))
          );
        }
      }

      setIsLoading(false);
    })();
  }, [kpiId, workplaceId, fromDate, toDate, daysBack]);

  const options = {
    chart: {
      type: 'column',
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: actualData?.map((entry) => entry.x.format(dateFormat)),
    },
    yAxis: {
      max: yAxisMax || null,
      title: {
        text: yAxisTitle || null,
      },
      labels: {
        formatter: (axis) => `${axis.value} ${unitOfMeasurement?.label || ''}`,
      },
    },
    legend: {
      enabled: showLegend,
    },
    series: [
      {
        type: 'column',
        name: isTargetLineDisabled
          ? store.keyPerformanceIndicatorStore.getById(kpiId)?.name
          : t('kpiChartCockpitWidget.chart.actual', {kpiName: store.keyPerformanceIndicatorStore.getById(kpiId)?.name}),
        data: actualData?.map((entry) => Number(entry.y.toFixed(2))),
      },
    ],
  };

  if (!isTargetLineDisabled) {
    options.series.push({
      type: 'line',
      name: t('kpiChartCockpitWidget.chart.target', {kpiName: store.keyPerformanceIndicatorStore.getById(kpiId)?.name}),
      data: targetData?.map((entry) => Number(entry.y.toFixed(2))),
    });
  }

  return (
    <Chart options={options} highcharts={Highcharts} isLoading={isLoading}/>
  );
};

export default observer(KpiChart);
